import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import biometricService from '../../services/biometricService';
import {
  captureFingerPrint,
  compareFingerprints,
  fetchAllFingerprints,
  getRegisteredMachine,
} from '../client-management/ClientApi';
import GlobalLoginModal from './GlobalLoginModal';
import EventLogOutModal from '../event-management/Events/EventLogOutModal';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const AutoBiometricLoginListener = () => {
  const [matchedUser, setMatchedUser] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [logoutClientId, setLogoutClientId] = useState(null);

  // Use ref to avoid unmounting/re-triggering the useEffect loop when modals open/close
  const isModalOpenRef = useRef(false);
  isModalOpenRef.current = isLoginModalOpen || isLogoutModalOpen;

  const getFingerprints = async () => {
    const cached = biometricService.getCachedFingerprints();
    if (cached && cached.length > 0) return cached;
    try {
      const res = await fetchAllFingerprints();
      const list = (res && res.fingerprints) ? res.fingerprints : (Array.isArray(res) ? res : []);
      biometricService.setCachedFingerprints(list);
      return list;
    } catch (e) {
      console.error("Error fetching fingerprints for auto-login:", e);
      return cached || [];
    }
  };

  const checkMachine = async () => {
    const cachedStatus = biometricService.getCachedMachineStatus();
    if (cachedStatus !== null) return cachedStatus;
    try {
      const res = await getRegisteredMachine();
      const isValid = !!(res?.success && res.data?.registeredMachineSN && res.data?.isActive);
      biometricService.setCachedMachineStatus(isValid);
      return isValid;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runAutoScanLoop = async () => {
      // Initial settling delay on startup
      await sleep(2500);

      while (isMounted) {
        // 1. Only run if admin is logged into the application
        const token = Cookies.get('token');
        if (!token) {
          await sleep(3000);
          continue;
        }

        // 2. Pause if tab is hidden / minimized in background
        if (typeof document !== 'undefined' && document.hidden) {
          await sleep(3000);
          continue;
        }

        // 3. Do not scan if manual scan (e.g. registration) is running or auto-scan is paused
        if (biometricService.isLocked() || biometricService.isPaused()) {
          await sleep(1000);
          continue;
        }

        // 4. Do not scan on customer allocation/registration page to avoid USB device conflicts
        if (typeof window !== 'undefined' && window.location.pathname === '/locker-safe-allocate') {
          await sleep(2000);
          continue;
        }

        // 5. Do not scan if any login modal is currently open on screen
        if (isModalOpenRef.current) {
          await sleep(1000);
          continue;
        }

        // 6. Verify machine status (uses 5-min cache)
        const isMachineReady = await checkMachine();
        if (!isMachineReady) {
          await sleep(10000);
          continue;
        }

        // 7. Setup abort controller for this listen cycle
        const controller = new AbortController();
        biometricService.setActiveController(controller);
        biometricService.setStatus('listening');

        try {
          const captureData = await captureFingerPrint(false, {
            timeout: '5000',
            signal: controller.signal,
            isAutoScan: true,
            skipMachineCheck: true, // Already verified by checkMachine() above
          });

          if (!isMounted) break;

          // 8. Check if a finger was detected on the sensor
          if (captureData && captureData.TemplateBase64) {
            biometricService.lockScanner('matching');
            biometricService.setStatus('matching');

            const fingerprints = await getFingerprints();
            let foundMatch = null;

            for (let i = 0; i < fingerprints.length; i++) {
              const user = fingerprints[i];
              if (!user.fingerPrintString) continue;

              try {
                const score = await compareFingerprints(
                  user.fingerPrintString,
                  captureData.TemplateBase64
                );
                if (score >= 80) {
                  foundMatch = user;
                  break;
                }
              } catch (err) {
                // Ignore single comparison error and check next user
              }
            }

            if (foundMatch) {
              foundMatch.serialNumber = captureData.SerialNumber;
              setMatchedUser(foundMatch);
              setIsLoginModalOpen(true);
              // Scanner stays locked while the login modal is open
            } else {
              toast.warning("Fingerprint detected, but no matching client found.");
              await sleep(2500); // 2.5s cooldown so user removes finger
              biometricService.unlockScanner();
            }
          } else {
            // No finger placed or device timeout
            if (captureData?.ErrorCode && captureData.ErrorCode !== 54) {
              // Device offline, detached or communication error -> wait 6 seconds
              await sleep(6000);
            } else {
              // Normal idle polling -> wait 2.5 seconds before next check
              await sleep(2500);
            }
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            // Aborted cleanly by manual registration scan or unmount
            await sleep(1000);
          } else {
            // Sensor connection or hardware offline, back off for 8 seconds
            await sleep(8000);
          }
        } finally {
          biometricService.setActiveController(null);
          if (!biometricService.isLocked()) {
            biometricService.setStatus('idle');
          }
        }
      }
    };

    runAutoScanLoop();

    return () => {
      isMounted = false;
      biometricService.setActiveController(null);
    };
  }, []);

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
    setMatchedUser(null);
    biometricService.unlockScanner();
  };

  const handleLoginEntrySuccess = (clientId) => {
    setIsLogoutModalOpen(true);
    setLogoutClientId(clientId);
  };

  const handleCloseLogoutModal = () => {
    setIsLogoutModalOpen(false);
    setLogoutClientId(null);
    biometricService.unlockScanner();
  };

  return (
    <>
      <GlobalLoginModal
        isOpen={isLoginModalOpen}
        onClose={handleCloseLoginModal}
        onLoginSuccess={() => {}}
        onLoginEntrySuccess={handleLoginEntrySuccess}
        matchedUser={matchedUser}
      />

      <EventLogOutModal
        isOpenLogin={isLogoutModalOpen}
        onCloseLOgin={handleCloseLogoutModal}
        clientId={logoutClientId}
      />
    </>
  );
};

export default AutoBiometricLoginListener;
